import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import debounce from 'lodash/debounce'
import { apiAgent } from '../../axiosConfig'
import { InputBox } from '../../design_system/components/Input Fields/InputBox.jsx'
import { Icons } from '../../design_system/components/Icons/Icons.jsx'

/**
 * EmailSuggestionInput — Notion-style email input with inline autocomplete.
 *
 * As the user types the current segment (after the last comma):
 *   Row 0: the raw typed text (always shown) — press Enter to accept as-is
 *   Row 1+: matching team members from /team_members search
 *
 * Selecting a suggestion or pressing Enter inserts the email and moves on.
 */
const EmailSuggestionInput = ({
    value,
    onChange,
    placeholder,
    error,
    helperText,
    showHelperText,
    customStyle,
}) => {
    const [suggestions, setSuggestions] = useState([])
    const [isOpen, setIsOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const containerRef = useRef(null)
    const currentSegmentRef = useRef('')

    // Extract the current segment (text after the last comma)
    const getCurrentSegment = useCallback((text) => {
        const parts = text.split(',')
        return (parts[parts.length - 1] || '').trim()
    }, [])

    // Already-entered emails — ref so debounce stays stable
    const enteredEmailsRef = useRef([])
    const enteredEmails = useMemo(() => (value || '').split(',').map(e => e.trim().toLowerCase()).filter(Boolean), [value])
    useEffect(() => { enteredEmailsRef.current = enteredEmails }, [enteredEmails])

    // Build the full options list: raw text row + team member matches
    const currentSegment = getCurrentSegment(value || '')
    useEffect(() => { currentSegmentRef.current = currentSegment }, [currentSegment])

    const allOptions = useMemo(() => {
        const opts = []
        // Row 0: raw typed text (only if there's something typed)
        if (currentSegment.length > 0) {
            opts.push({ id: '__raw__', name: null, email: currentSegment, isRaw: true })
        }
        // Row 1+: team member matches
        opts.push(...suggestions)
        return opts
    }, [currentSegment, suggestions])

    // Debounced search — stable, never recreated
    const debouncedSearch = useMemo(() => {
        return debounce((query) => {
            if (query.length < 2) {
                setSuggestions([])
                return
            }
            apiAgent({
                method: 'get',
                url: '/team_members',
                params: {
                    name_query: query,
                    approval_status: 'accepted',
                    from_filters: true,
                    page_size: 5,
                },
            })
                .then(res => {
                    const entered = enteredEmailsRef.current
                    const members = (res.data.data || [])
                        .filter(m => m.user?.email)
                        .filter(m => !entered.includes(m.user.email.toLowerCase()))
                        .map(m => ({
                            id: m.id,
                            name: m.user.name,
                            email: m.user.email,
                        }))
                    setSuggestions(members)
                })
                .catch(() => setSuggestions([]))
        }, 300)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const hasTypedRef = useRef(false)

    // Show/hide dropdown — only after user has typed
    useEffect(() => {
        if (!hasTypedRef.current) return
        setIsOpen(currentSegment.length > 0)
        setActiveIndex(0)
    }, [currentSegment])

    // On input change
    const handleChange = (e) => {
        hasTypedRef.current = true
        const newValue = e.target.value
        onChange(newValue)
        const segment = getCurrentSegment(newValue)
        debouncedSearch(segment)
    }

    // Accept an option — replace current segment with the email
    const acceptOption = useCallback((option) => {
        const email = option.email
        const parts = (value || '').split(',').map(e => e.trim()).filter(Boolean)
        // Remove the incomplete segment the user was typing
        if (currentSegmentRef.current) parts.pop()
        parts.push(email)
        // Add trailing comma only for team member picks, not raw text
        const newValue = option.isRaw ? parts.join(', ') : parts.join(', ') + ', '
        onChange(newValue)
        setSuggestions([])
        setIsOpen(false)
        const input = containerRef.current?.querySelector('input')
        if (input) setTimeout(() => input.focus(), 0)
    }, [value, onChange])

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen || allOptions.length === 0) return

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActiveIndex(i => (i + 1) % allOptions.length)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActiveIndex(i => (i - 1 + allOptions.length) % allOptions.length)
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            if (allOptions[activeIndex]) {
                e.preventDefault()
                acceptOption(allOptions[activeIndex])
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false)
        }
    }

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div ref={containerRef} className="email-suggestion" style={customStyle}>
            <InputBox
                Size="Large"
                label="To"
                showLabel
                error={error}
                helperText={helperText}
                showHelperText={showHelperText}
            >
                <input
                    type="text"
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={() => setTimeout(() => setIsOpen(false), 150)}
                    onFocus={() => { if (hasTypedRef.current && currentSegmentRef.current.length > 0) setIsOpen(true) }}
                    placeholder={placeholder}
                />
            </InputBox>
            {isOpen && allOptions.length > 0 && (
                <div className="email-suggestion__dropdown">
                    {allOptions.map((option, i) => (
                        <div
                            key={option.id}
                            className={`email-suggestion__option${i === activeIndex ? ' email-suggestion__option--active' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); acceptOption(option) }}
                            onMouseEnter={() => setActiveIndex(i)}
                        >
                            {option.isRaw ? (
                                <div className="email-suggestion__raw">
                                    <Icons name="mail" size={16} />
                                    <span className="body-m color-text-secondary">{option.email}</span>
                                </div>
                            ) : (
                                <div className="email-suggestion__member">
                                    <Icons name="user-circle" size={16} />
                                    <div className="email-suggestion__member-info">
                                        <span className="body-m-semibold email-suggestion__name">{option.name}</span>
                                        <span className="body-m email-suggestion__email">{option.email}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default EmailSuggestionInput
